<template>
    <div :class="cellClasses" @click="navigateToDaily">
        <div class="avail-day__header">
            <span :class="dayNumberClasses">{{ date.format('D') }}</span>
            <span v-if="isToday" class="avail-day__today-badge">{{ $t('pages.referrals.new.microcopy.today') }}</span>
            <span v-if="checkForActions" class="avail-day__action-dot"></span>
        </div>

        <div class="avail-day__badges">
            <template v-if="hasAvailableSlots">
                <span v-if="confirmedCount > 0" class="avail-day__badge avail-day__badge--confirmed">
                    {{ $t('pages.availability.index.calendar.confirmed_count', { count: confirmedCount }) }}
                </span>
                <span v-if="pendingCount > 0" class="avail-day__badge avail-day__badge--pending">
                    {{ $t('pages.availability.index.calendar.pending_count', { count: pendingCount }) }}
                </span>
                <span v-if="availableSlotsCount > 0" class="avail-day__badge avail-day__badge--available">
                    {{ $t('pages.availability.index.calendar.available') }}
                </span>
            </template>

            <span v-if="showNotAvailable" class="avail-day__badge avail-day__badge--not-available">
                <span class="avail-day__badge-text--strikethrough">{{ $t('pages.availability.index.calendar.available') }}</span>
            </span>
        </div>
    </div>
</template>

<script>
    import dayjs from 'dayjs'

    export default {
        props: {
            year: { type: Number, required: true },
            month: { type: Number, required: true },
            day: { type: Number, required: true },
            slots: { type: Array, required: true },
            slotsCount: { type: Number, default: 0 },
            referralsCount: { type: Number, required: true },
            confirmedCount: { type: Number, default: 0 },
            pendingCount: { type: Number, default: 0 },
            isUnavailable: { type: Boolean, default: false },
            showCancelled: { type: Boolean, required: true },
        },

        computed: {
            date() {
                return dayjs().year(this.year).month(this.month).date(this.day).startOf('day')
            },

            isToday() {
                return this.date.isSame(dayjs().startOf('day'), 'day')
            },

            hasAvailableSlots() {
                return this.slotsCount || this.referralsCount
            },

            availableSlotsCount() {
                const unavailableSlotsCount = this.slots.filter((slot) => slot.isUnavailable).length
                return this.slots.length - this.referralsCount - unavailableSlotsCount
            },

            showNotAvailable() {
                if (this.slots.length === 0) return true
                if (this.isUnavailable && !this.confirmedCount && !this.pendingCount) return true
                return !this.hasAvailableSlots && !this.confirmedCount && !this.pendingCount
            },

            cellClasses() {
                return {
                    'avail-day': true,
                    'avail-day--today': this.isToday,
                    'avail-day--not-available': this.showNotAvailable && !this.isToday,
                }
            },

            dayNumberClasses() {
                return {
                    'avail-day__num': true,
                    'avail-day__num--today': this.isToday,
                }
            },

            checkForActions() {
                return (
                    this.slots.filter(
                        (slot) =>
                            slot.referral &&
                            (slot.referral.status === 'CREATED' ||
                                slot.referral.status === 'INSURANCE_AUTH_PENDING' ||
                                slot.referral.status === 'CONSULTATION_NOTES_PENDING'),
                    ).length > 0
                )
            },
        },

        methods: {
            navigateToDaily() {
                const dateStr = this.date.format('YYYY-MM-DD')
                this.$router
                    .push({
                        name: this.$route.name,
                        params: this.$route.params,
                        query: {
                            ...this.$route.query,
                            view: 'daily',
                            date: dateStr,
                        },
                    })
                    .catch(() => {})
            },
        },
    }
</script>

<style scoped>
    .avail-day {
        position: relative;
        background: #ffffff;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        padding: 12px;
        min-height: 130px;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        cursor: pointer;
        transition: box-shadow 0.15s ease, border-color 0.15s ease, background 0.15s ease;
    }

    .avail-day:hover {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }

    .avail-day--today {
        background: #ffffff;
        border-color: #3b82f6;
    }

    .avail-day--today:hover {
        border-color: #2563eb;
    }

    .avail-day--today.avail-day--not-available {
        background: #ffffff;
        border-color: #3b82f6;
    }

    .avail-day--not-available {
        border-color: #e5e7eb;
        background: #ffffff;
    }

    .avail-day__header {
        display: flex;
        align-items: center;
        gap: 6px;
    }

    .avail-day__num {
        font-size: 14px;
        font-weight: 500;
        color: #1f2937;
        line-height: 1;
    }

    .avail-day__num--today {
        color: #2563eb;
        font-weight: 600;
    }

    .avail-day__today-badge {
        font-size: 14px;
        font-weight: 600;
        line-height: 1;
        color: #2563eb;
    }

    .avail-day__action-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #ef4444;
        flex-shrink: 0;
        margin-left: auto;
    }

    .avail-day__badges {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;
        padding-top: 8px;
    }

    .avail-day__badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        max-width: 96px;
        min-height: 20px;
        font-size: 12px;
        font-weight: 400;
        line-height: 1.2;
        padding: 2px 10px;
        border-radius: 4px;
        text-align: center;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        box-sizing: border-box;
    }

    .avail-day__badge--confirmed {
        background: #22c55e;
        color: #ffffff;
    }

    .avail-day__badge--pending {
        background: #f97316;
        color: #ffffff;
    }

    .avail-day__badge--available {
        background: #e5e7eb;
        color: #4b5563;
    }

    .avail-day__badge--not-available {
        background: #e5e7eb;
        border: 1px dashed #9ca3af;
        color: #4b5563;
    }

    .avail-day__badge-text--strikethrough {
        text-decoration: line-through;
    }

    @media (max-width: 1024px) {
        .avail-day {
            padding: 10px;
            min-height: 110px;
        }

        .avail-day__badge {
            font-size: 11px;
            width: 100%;
            max-width: 96px;
            min-height: 18px;
            padding: 2px 8px;
        }
    }

    @media (max-width: 768px) {
        .avail-day {
            padding: 8px;
            min-height: 90px;
            border-radius: 6px;
        }

        .avail-day__num {
            font-size: 12px;
        }

        .avail-day__today-badge {
            font-size: 8px;
            padding: 1px 4px;
        }

        .avail-day__badge {
            font-size: 9px;
            width: 100%;
            max-width: 96px;
            min-height: 18px;
            padding: 1px 8px;
        }

        .avail-day__badges {
            gap: 3px;
        }
    }
</style>
